import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Logo } from "../components/Logo";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Send, Plus, Brain, ArrowLeft, Menu } from "lucide-react";

export default function Companion() {
    const { t, user } = useApp();
    const nav = useNavigate();
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sidebar, setSidebar] = useState(false);
    const endRef = useRef(null);

    const loadConvs = useCallback(async () => {
        const { data } = await api.get("/conversations");
        setConvs(data.conversations);
        return data.conversations;
    }, []);

    const openConv = useCallback(async (id) => {
        setActive(id); setSidebar(false);
        const { data } = await api.get(`/conversations/${id}/messages`);
        setMessages(data.messages);
    }, []);

    const newConv = useCallback(async () => {
        const { data } = await api.post("/conversations");
        setConvs((c) => [data.conversation, ...c]);
        setActive(data.conversation.conv_id);
        setMessages([]);
        setSidebar(false);
    }, []);

    useEffect(() => {
        (async () => {
            const list = await loadConvs();
            if (list.length) await openConv(list[0].conv_id);
            else await newConv();
            setLoading(false);
        })();
    }, [loadConvs, openConv, newConv]);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

    const send = async (preset) => {
        const content = (preset ?? text).trim();
        if (!content || sending || !active) return;
        setText("");
        setMessages((m) => [...m, { role: "user", content, msg_id: "tmp-" + Date.now() }]);
        setSending(true);
        try {
            const { data } = await api.post(`/conversations/${active}/messages`, { text: content });
            setMessages((m) => [...m, data.message]);
            loadConvs();
        } catch (e) {
            toast.error(apiErr(e.response?.data?.detail) || t("chat.retry"));
            setText(content);
        }
        setSending(false);
    };

    const suggestions = [t("chat.suggest1"), t("chat.suggest2"), t("chat.suggest3")];

    if (loading) return <div className="min-h-[70vh] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-forest/20 border-t-forest animate-spin" /></div>;

    return (
        <div className="max-w-6xl mx-auto px-0 sm:px-6 h-[calc(100vh-4rem)] flex">
            {/* Sidebar */}
            <aside className={`${sidebar ? "block" : "hidden"} md:block w-full md:w-64 shrink-0 border-r border-charcoal/10 bg-white/50 absolute md:relative z-20 h-[calc(100vh-4rem)] md:h-auto`}>
                <div className="p-4">
                    <button data-testid="new-conversation" onClick={newConv} className="btn-primary w-full py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2"><Plus size={16} /> {t("chat.new")}</button>
                </div>
                <div className="px-2 space-y-1 overflow-y-auto">
                    {convs.map((c) => (
                        <button key={c.conv_id} onClick={() => openConv(c.conv_id)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm truncate ${active === c.conv_id ? "bg-forest/10 text-forest font-medium" : "hover:bg-charcoal/5 text-charcoal/75"}`}>
                            {c.title}
                        </button>
                    ))}
                </div>
            </aside>

            {/* Chat */}
            <section className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-charcoal/10 bg-ivory/80 backdrop-blur">
                    <div className="flex items-center gap-2">
                        <button className="md:hidden p-1.5" onClick={() => setSidebar(!sidebar)}><Menu size={18} /></button>
                        <div className="text-base"><Logo /></div>
                        <span className="text-sm text-charcoal/70 hidden sm:inline">· {t("chat.title")}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {user?.consents?.memory && <span className="inline-flex items-center gap-1 text-xs text-forest bg-forest/8 px-2.5 py-1 rounded-full"><Brain size={12} /> Memory on</span>}
                        <button onClick={() => nav("/report")} className="text-xs text-charcoal/60 hover:text-forest inline-flex items-center gap-1"><ArrowLeft size={13} /> {t("nav.report")}</button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
                    <div className="mx-auto max-w-md text-center text-xs text-charcoal/55 bg-white/60 rounded-xl p-3 border border-charcoal/10">{t("chat.disclaimer")}</div>

                    {messages.length === 0 && (
                        <div className="max-w-lg mx-auto mt-6">
                            <p className="text-sm text-charcoal/70 mb-3">{t("chat.empty")}</p>
                            <div className="grid gap-2">
                                {suggestions.map((sug) => (
                                    <button key={sug} data-testid="chat-suggestion" onClick={() => send(sug)} className="text-left text-sm p-3 rounded-xl border border-charcoal/15 hover:bg-charcoal/5 bg-white">{sug}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((m) => (
                        <div key={m.msg_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] sm:max-w-[72%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-forest text-ivory rounded-br-md" : "bg-white border border-charcoal/10 text-charcoal rounded-bl-md"}`}>
                                {m.content}
                            </div>
                        </div>
                    ))}
                    {sending && <div className="flex justify-start"><div className="bg-white border border-charcoal/10 px-4 py-3 rounded-2xl rounded-bl-md text-sm text-charcoal/60 italic">{t("chat.thinking")}</div></div>}
                    <div ref={endRef} />
                </div>

                <div className="border-t border-charcoal/10 p-3 sm:p-4 bg-ivory">
                    <div className="flex items-end gap-2 max-w-3xl mx-auto">
                        <textarea data-testid="ai-chat-input-field" rows={1} value={text} onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder={t("chat.placeholder")}
                            className="flex-1 resize-none rounded-2xl border border-charcoal/15 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 max-h-32" />
                        <button data-testid="chat-send-button" onClick={() => send()} disabled={sending || !text.trim()}
                            className="btn-primary h-12 w-12 flex items-center justify-center shrink-0 disabled:opacity-50"><Send size={18} /></button>
                    </div>
                </div>
            </section>
        </div>
    );
}
